import { RateLimitingErrorType } from "@next-platform/packages-ts-types";
import { formatNumber } from "../../utils/formatNumber";
import { getErrorType } from "../../utils/getErrorType";
import { useCallback } from "react";
import { SubscriptionStatus, useUserInfo } from "../";
import { useTranslation } from "../localization/hooks";
import { TextMessagesUserLimit } from "./constants";
import { getRateLimitErrorMessage } from "./getRateLimitErrorMessage";
import { RateLimitFlows } from "./types";
// eslint-disable-next-line max-lines-per-function -- exhaustive rate-limit error-type ├ù flow switch
export function useRateLimitErrors() {
    const { status: subscriptionStatus, isAuthenticated } = useUserInfo();
    const isSubscriptionActive = subscriptionStatus === SubscriptionStatus.ACTIVE;
    const { t } = useTranslation();
    const RATE_LIMIT_ERROR_MESSAGES = getRateLimitErrorMessage(t);
    const getMessage = useCallback(({ errorCode, errorContext, skipDefaultMessage }) => {
        const errorType = getErrorType(errorCode, RateLimitingErrorType);
        const { flow } = errorContext || {};
        switch (errorType) {
            case RateLimitingErrorType.RATE_LIMIT_BREACHED:
                switch (flow) {
                    case RateLimitFlows.GENERATE_IMAGE:
                    case RateLimitFlows.GENERATE_CONVERSATION_IMAGE:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_GENERATE_IMAGE_MESSAGE;
                    case RateLimitFlows.CHAT:
                        if (isSubscriptionActive) {
                            return t("error:rateLimit.breached.chatPaid", {
                                limit: formatNumber(TextMessagesUserLimit.PREMIUM),
                            });
                        }
                        if (isAuthenticated) {
                            return t("error:rateLimit.breached.chatFree", {
                                limit: formatNumber(TextMessagesUserLimit.FREE),
                            });
                        }
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_CHAT_GUEST;
                    case RateLimitFlows.CREATE_CHATBOT:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_CREATE_CHATBOT;
                    case RateLimitFlows.TRANSLATE_CHATBOT:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_TRANSLATE_CHATBOT;
                    case RateLimitFlows.REPORT_CHATBOT:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_REPORT_CHATBOT;
                    case RateLimitFlows.UPLOAD_USER_IMAGE:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_UPLOAD_USER_IMAGE;
                    case RateLimitFlows.SUBSCRIBE:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_SUBSCRIBE;
                    case RateLimitFlows.SUGGEST_FIELD:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_SUGGEST_FIELD;
                    case RateLimitFlows.SUGGEST_TAGS:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_SUGGEST_TAGS;
                    case RateLimitFlows.PLAY_MESSAGE_AUDIO:
                        return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_PLAY_MESSAGE_AUDIO;
                    default:
                        if (skipDefaultMessage)
                            return "";
                        return t("error:fallbackError");
                }
            case RateLimitingErrorType.TIME_UNIT_MUST_BE_DEFINED:
                switch (flow) {
                    case RateLimitFlows.GENERATE_IMAGE:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_GENERATE_IMAGE;
                    case RateLimitFlows.CHAT:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_CHAT;
                    case RateLimitFlows.CREATE_CHATBOT:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_CREATE_CHATBOT;
                    case RateLimitFlows.TRANSLATE_CHATBOT:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_TRANSLATE_CHATBOT;
                    case RateLimitFlows.REPORT_CHATBOT:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_REPORT_CHATBOT;
                    case RateLimitFlows.UPLOAD_USER_IMAGE:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_UPLOAD_USER_IMAGE;
                    case RateLimitFlows.SUBSCRIBE:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_SUBSCRIBE;
                    case RateLimitFlows.SUGGEST_FIELD:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_SUGGEST_FIELD;
                    case RateLimitFlows.SUGGEST_TAGS:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_SUGGEST_TAGS;
                    case RateLimitFlows.PLAY_MESSAGE_AUDIO:
                        return RATE_LIMIT_ERROR_MESSAGES.TIME_UNIT_MUST_BE_DEFINED_PLAY_MESSAGE_AUDIO;
                    default:
                        if (skipDefaultMessage)
                            return "";
                        return t("error:fallbackError");
                }
            default:
                if (skipDefaultMessage)
                    return "";
                return t("error:fallbackError");
        }
    }, [isSubscriptionActive, isAuthenticated, RATE_LIMIT_ERROR_MESSAGES, t]);
    const getTitle = useCallback(({ errorCode, errorContext }) => {
        const errorType = getErrorType(errorCode, RateLimitingErrorType);
        const { flow } = errorContext || {};
        if (errorType !== RateLimitingErrorType.RATE_LIMIT_BREACHED)
            return "";
        switch (flow) {
            case RateLimitFlows.GENERATE_IMAGE:
            case RateLimitFlows.GENERATE_CONVERSATION_IMAGE:
                return RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_GENERATE_IMAGE_TITLE;
            default:
                return "";
        }
    }, [RATE_LIMIT_ERROR_MESSAGES]);
    return { getMessage, getTitle };
}
